import React from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, CalendarDays, MapPin, Ban, HelpCircle, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { EXECUTION_LABELS, SERVICES_META } from "@/lib/constants";
import { formatDatePt } from "@/lib/format";

const Field = ({ label, value }) => (value ? (
  <p className="text-sm text-[color:var(--brand-graphite)]"><span className="text-[color:var(--brand-muted)]">{label}:</span> {value}</p>
) : null);

const ModuleCard = ({ title, children }) => (
  <div className="mt-4 rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
    <h3 className="font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">{title}</h3>
    <div className="mt-2 space-y-1">{children}</div>
  </div>
);

export const ReviewStep = ({ event, confirmed, onConfirmChange }) => {
  const rec = event.reception || {};
  const styles = [...(rec.styles || [])];
  if (rec.other_style) styles.push(rec.other_style);
  const moments = [...(event.moments || [])].sort((a, b) => a.order - b.order);
  const svc = event.services || { cerimonia: true };
  const banda = event.banda || {};
  const astro = event.astrolive || {};
  const festa = event.festa || {};
  const prods = event.astrolive_products || {};
  const bStyles = [...(banda.estilos || [])];
  if (banda.estilos_outros) bStyles.push(banda.estilos_outros);
  const activeSvcs = Object.keys(SERVICES_META).filter((k) => svc[k]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="mx-auto max-w-2xl"
    >
      <h2 className="font-serif text-2xl font-semibold text-[color:var(--brand-graphite)] sm:text-3xl">
        Confira as informações do seu evento
      </h2>
      <p className="mt-2 text-sm text-[color:var(--brand-muted)]">
        Revise com carinho antes de enviar para a LC Produção.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {activeSvcs.map((k) => (
          <span key={k} className="rounded-full bg-[color:var(--brand-marsala-soft)] px-3 py-1 text-xs font-medium text-[color:var(--brand-marsala)]">{SERVICES_META[k].label}</span>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
        <p className="font-serif text-2xl font-semibold text-[color:var(--brand-graphite)]">{event.couple_name}</p>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-[color:var(--brand-muted)]">
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {formatDatePt(event.date)}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {event.local}</span>
        </div>
        {(event.formation?.type || (event.formation?.instruments || []).length > 0) && (
          <p className="mt-3 border-t border-[color:var(--brand-border)] pt-3 text-sm text-[color:var(--brand-graphite)]">
            {event.formation?.type && <span><span className="text-[color:var(--brand-muted)]">Formação:</span> {event.formation.type}</span>}
            {(event.formation?.instruments || []).length > 0 && (
              <span className="block"><span className="text-[color:var(--brand-muted)]">Instrumentos:</span> {event.formation.instruments.join(", ")}</span>
            )}
          </p>
        )}
      </div>

      {svc.cerimonia && (<>
      <div className="mt-4 rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
        <h3 className="font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">Recepção dos convidados</h3>
        {styles.length > 0 ? (
          <p className="mt-2 text-sm text-[color:var(--brand-graphite)]">{styles.join("  •  ")}</p>
        ) : (
          <p className="mt-2 text-sm text-[color:var(--brand-muted)]">Nenhum estilo selecionado.</p>
        )}
        {rec.references && (
          <p className="mt-2 text-sm text-[color:var(--brand-muted)]"><span className="font-medium text-[color:var(--brand-graphite)]">Referências:</span> {rec.references}</p>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {moments.map((m, i) => {
          const music = m.music || {};
          return (
            <div key={m.id} data-testid={`review-moment-${m.id}`} className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-4">
              <div className="flex items-center justify-between">
                <h4 className="font-serif text-base font-semibold text-[color:var(--brand-graphite)]">{i + 1}. {m.name}</h4>
                {m.surprise?.is && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-marsala)] px-2 py-0.5 text-[10px] font-semibold text-white">
                    <Star className="h-3 w-3 fill-white" /> Música surpresa
                  </span>
                )}
              </div>
              {m.not_applicable ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-[color:var(--brand-muted)]"><Ban className="h-4 w-4" /> Não se aplica</p>
              ) : m.undecided ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-[color:var(--brand-warning)]"><HelpCircle className="h-4 w-4" /> Ainda não decidido</p>
              ) : (
                <div className="mt-2 space-y-1 text-sm">
                  {music.track_name && (
                    <p className="text-[color:var(--brand-graphite)]"><span className="text-[color:var(--brand-muted)]">Música:</span> {music.track_name}{music.artist_name ? ` — ${music.artist_name}` : ""}</p>
                  )}
                  {m.execution_type && (
                    <p className="text-[color:var(--brand-graphite)]"><span className="text-[color:var(--brand-muted)]">Execução:</span> {EXECUTION_LABELS[m.execution_type]}{m.execution_detail ? ` — ${m.execution_detail}` : ""}</p>
                  )}
                  {(m.manual_link || music.link) && (
                    <a href={m.manual_link || music.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[color:var(--brand-marsala)] hover:underline">
                      <ExternalLink className="h-3.5 w-3.5" /> Abrir referência
                    </a>
                  )}
                  {m.observations && <p className="text-[color:var(--brand-muted)]"><span className="font-medium">Obs:</span> {m.observations}</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>)}

      {svc.banda && (
        <ModuleCard title="Banda / Receptivo">
          {bStyles.length > 0 && <Field label="Estilos" value={bStyles.join(" • ")} />}
          <Field label="Artistas de referência" value={banda.musicas_desejadas} />
          <Field label="Não desejam ouvir" value={banda.musicas_nao_desejadas} />
          <Field label="Perfil" value={banda.perfil === "Outro" ? banda.perfil_outro : banda.perfil} />
        </ModuleCard>
      )}

      {svc.astrolive && (
        <ModuleCard title="AstroLive">
          <Field label="Intuito do show" value={astro.intuito === "Outro." ? astro.intuito_outro : astro.intuito} />
          <Field label="Perfil musical" value={astro.perfil_evento} />
          <Field label="Músicas desejadas" value={astro.musicas_desejadas} />
          {astro.musicas_nao_desejadas && (
            <p className="text-sm font-medium text-[color:var(--brand-danger)]"><AlertTriangle className="mr-1 inline h-3.5 w-3.5" />Não tocar: {astro.musicas_nao_desejadas}</p>
          )}
          <Field label="Nível de interação" value={astro.interacao} />
          {prods.bazuca && <Field label="Bazuca CO₂" value={astro.bazuca_autorizada === "sim" ? "Autorizada" : "Não autorizada"} />}
          {prods.areia && <Field label="Areia nos tambores" value={astro.areia_autorizada === "sim" ? "Autorizada" : "Não autorizada"} />}
          <Field label="Observações" value={astro.observacoes} />
        </ModuleCard>
      )}

      {(svc.banda || svc.astrolive) && (
        <ModuleCard title="Dados da festa">
          <Field label="Início da festa" value={festa.inicio} />
          <Field label="Horário do show" value={festa.show_undefined ? "Ainda não definido" : festa.show} />
          <Field label="Encerramento" value={festa.encerramento} />
          {festa.outra_atracao && <Field label="Outra atração" value={(festa.outra_atracao_nome || "Sim") + (festa.outra_atracao_horario ? ` (${festa.outra_atracao_horario})` : "")} />}
          <Field label="Sonorização LC" value={festa.sonorizacao === "sim" ? "Sim" : festa.sonorizacao === "nao" ? "Não" : festa.sonorizacao === "nao_sei" ? "A confirmar" : ""} />
          <Field label="Palco no local" value={festa.possui_palco} />
        </ModuleCard>
      )}
      <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl border border-[color:var(--brand-border)] bg-[color:var(--brand-paper)] p-4">
        <Checkbox data-testid="review-confirm-checkbox" checked={confirmed} onCheckedChange={onConfirmChange} className="mt-0.5" />
        <span className="text-sm text-[color:var(--brand-graphite)]">
          Confirmo que revisei as informações acima e que as músicas, versões e referências estão corretas.
        </span>
      </label>
    </motion.div>
  );
};
